/**
 * NewsPulseAirportProfile — rich public profile for a single airport.
 * Consumes /api/newspulse/v1/aviation/airport/{id}.
 *
 * Sections: hero (name, IATA/ICAO, location, type), core stats,
 * routes served, hub airlines, latest news events, related airports.
 */
import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import {
  Plane, MapPin, Globe2, Building2, ArrowRight, Network, TrendingUp, Newspaper,
} from "lucide-react";
import { npApi } from "./api";
import EventCard from "./EventCard";

function StatBox({ label, value, icon: Icon }) {
  return (
    <div className="rounded-xl bg-white border border-slate-200 p-4 flex items-center gap-3">
      <span className="rounded-lg bg-sky-50 p-2 text-sky-700">
        <Icon className="w-5 h-5" />
      </span>
      <div>
        <div className="text-lg font-bold text-slate-900">{value}</div>
        <div className="text-[11px] uppercase tracking-widest text-slate-500">{label}</div>
      </div>
    </div>
  );
}

function AirlinePill({ airline }) {
  return (
    <Link
      to={`/aviation/airline/${airline.airline_id}`}
      className="inline-flex items-center gap-2 rounded-full border border-slate-200 bg-white px-3 py-1.5 text-sm hover:border-violet-400 hover:text-violet-700 transition-colors"
      data-testid={`airport-airline-pill-${airline.airline_id}`}
    >
      <span className="inline-flex rounded-md bg-violet-50 border border-violet-100 text-violet-700 text-[10px] font-bold px-1.5 py-0.5">
        {airline.iata || airline.icao || "—"}
      </span>
      <span className="font-medium text-slate-800">{airline.canonical_name}</span>
    </Link>
  );
}

function RouteRow({ route, airportId }) {
  const isOrigin = route.origin_airport_id === airportId;
  const other = isOrigin
    ? { iata: route.destination_iata, id: route.destination_airport_id }
    : { iata: route.origin_iata, id: route.origin_airport_id };
  return (
    <Link
      to={`/aviation/airport/${other.id}`}
      className="flex items-center justify-between gap-3 py-2.5 px-3 rounded-lg hover:bg-slate-50 transition-colors"
      data-testid={`airport-route-row-${route.route_id}`}
    >
      <div className="flex items-center gap-2 min-w-0">
        <span className="inline-flex items-center gap-1 text-xs text-slate-500 shrink-0">
          {isOrigin ? "→" : "←"} <ArrowRight className="w-3 h-3" />
        </span>
        <span className="inline-flex rounded-md bg-slate-100 text-slate-700 text-[10px] font-bold px-1.5 py-0.5 shrink-0">
          {other.iata}
        </span>
        <span className="text-sm text-slate-800 truncate">{route.canonical_name}</span>
      </div>
      <span className={`text-[10px] font-semibold uppercase tracking-widest shrink-0 ${route.is_international ? "text-amber-600" : "text-emerald-600"}`}>
        {route.is_international ? "Intl" : "Domestic"}
      </span>
    </Link>
  );
}

export default function NewsPulseAirportProfile() {
  const { airportId } = useParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError("");
    npApi
      .airport(airportId)
      .then((r) => { if (!cancelled) setData(r); })
      .catch((e) => { if (!cancelled) setError(e?.message || "load_failed"); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [airportId]);

  if (loading) {
    return (
      <div className="max-w-[1280px] mx-auto px-4 lg:px-6 py-16 text-slate-500" data-testid="airport-profile-loading">
        Loading airport…
      </div>
    );
  }
  if (error || !data) {
    return (
      <div className="max-w-[1280px] mx-auto px-4 lg:px-6 py-16" data-testid="airport-profile-error">
        <h1 className="text-2xl font-bold text-slate-900 mb-2">Airport not found</h1>
        <p className="text-slate-500 mb-4">We couldn't find "{airportId}" in the aviation graph.</p>
        <Link to="/aviation" className="inline-flex items-center gap-1.5 text-blue-700 hover:text-blue-800 font-semibold">
          ← Back to Aviation Hub
        </Link>
      </div>
    );
  }

  const a = data.airport;
  const routes = data.routes || [];
  const hubAirlines = data.hub_airlines || [];
  const operatingAirlines = data.operating_airlines || [];
  const relatedAirports = data.related_airports || [];
  const events = data.recent_events || [];
  const fallbackEvents = data.fallback_events || [];

  return (
    <div className="max-w-[1280px] mx-auto px-4 lg:px-6 py-8" data-testid="airport-profile">
      {/* Breadcrumb */}
      <nav className="text-xs text-slate-500 mb-3">
        <Link to="/aviation" className="hover:text-blue-700">Aviation Hub</Link>
        <span className="mx-1.5">/</span>
        <span className="text-slate-700 font-medium">{a.canonical_name}</span>
      </nav>

      {/* Hero */}
      <section className="rounded-2xl bg-gradient-to-br from-slate-900 via-blue-900 to-sky-800 p-8 md:p-12 text-white mb-6 relative overflow-hidden">
        <div className="absolute -right-6 -bottom-10 opacity-10">
          <Plane className="w-56 h-56" strokeWidth={0.6} />
        </div>
        <div className="relative">
          <div className="flex items-center gap-2 mb-3">
            <span className="inline-flex rounded-md bg-white/15 backdrop-blur-sm text-white text-xs font-bold px-2 py-0.5 border border-white/20">
              {a.iata || "—"}
            </span>
            <span className="text-[10px] uppercase tracking-widest text-sky-200">
              {a.icao || ""}
            </span>
            {a.airport_type && (
              <span className="inline-flex rounded-full bg-white/10 border border-white/15 text-[10px] font-semibold uppercase tracking-widest text-sky-200 px-2 py-0.5">
                {a.airport_type.replace("_", " ")}
              </span>
            )}
          </div>
          <h1 className="text-3xl sm:text-4xl md:text-5xl font-bold leading-tight max-w-4xl" data-testid="airport-profile-name">
            {a.canonical_name}
          </h1>
          <p className="mt-3 text-sky-100 inline-flex items-center gap-1.5">
            <MapPin className="w-4 h-4" />
            {a.city}
            {a.state && `, ${a.state}`}
            {a.country_name && `, ${a.country_name}`}
          </p>
          {(a.aliases || []).length > 0 && (
            <p className="mt-2 text-xs text-sky-200/80">
              Also known as: {(a.aliases || []).join(" · ")}
            </p>
          )}
        </div>
      </section>

      {/* Core stats */}
      <section className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-8" data-testid="airport-stats">
        <StatBox label="Routes served" value={routes.length} icon={Network} />
        <StatBox label="Hub airlines" value={hubAirlines.length} icon={Building2} />
        <StatBox label="Airlines here" value={operatingAirlines.length || hubAirlines.length} icon={Plane} />
        <StatBox label="Recent stories" value={events.length} icon={Newspaper} />
      </section>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
        {/* Left: news + routes */}
        <div className="lg:col-span-2 space-y-8">
          {/* News */}
          <section data-testid="airport-news">
            <div className="pb-3 mb-4 border-b border-slate-200">
              <h2 className="text-xl font-bold text-slate-900 inline-flex items-center gap-2">
                <Newspaper className="w-5 h-5 text-blue-600" /> Latest stories
              </h2>
            </div>
            {events.length ? (
              <div>
                {events.map((e) => <EventCard key={e.event_id} event={e} />)}
              </div>
            ) : fallbackEvents.length ? (
              <>
                <p className="text-xs text-slate-500 italic mb-3">
                  No stories tagged to this airport yet — showing the latest across all aviation.
                </p>
                <div data-testid="airport-fallback-news">
                  {fallbackEvents.map((e) => <EventCard key={e.event_id} event={e} compact />)}
                </div>
              </>
            ) : (
              <p className="text-slate-500 italic text-sm">
                No stories yet tagged to this airport. NewsPulse will begin populating this
                feed as aviation events are ingested.
              </p>
            )}
          </section>

          {/* Routes */}
          <section data-testid="airport-routes">
            <div className="pb-3 mb-4 border-b border-slate-200">
              <h2 className="text-xl font-bold text-slate-900 inline-flex items-center gap-2">
                <Network className="w-5 h-5 text-sky-600" /> Routes served
              </h2>
            </div>
            {routes.length ? (
              <div className="rounded-xl border border-slate-200 bg-white divide-y divide-slate-100">
                {routes.slice(0, 40).map((r) => (
                  <RouteRow key={r.route_id} route={r} airportId={a.airport_id} />
                ))}
              </div>
            ) : (
              <p className="text-slate-500 italic text-sm">
                No canonical routes registered for this airport yet.
              </p>
            )}
          </section>
        </div>

        {/* Right: sidebar */}
        <aside className="space-y-6">
          {hubAirlines.length > 0 && (
            <section className="rounded-xl border border-slate-200 bg-white p-5">
              <h3 className="text-sm font-bold uppercase tracking-widest text-slate-500 mb-3 inline-flex items-center gap-2">
                <Building2 className="w-4 h-4" /> Hub airlines
              </h3>
              <div className="flex flex-wrap gap-2">
                {hubAirlines.map((al) => <AirlinePill key={al.airline_id} airline={al} />)}
              </div>
            </section>
          )}

          {operatingAirlines.length > 0 && (
            <section className="rounded-xl border border-slate-200 bg-white p-5">
              <h3 className="text-sm font-bold uppercase tracking-widest text-slate-500 mb-3 inline-flex items-center gap-2">
                <Plane className="w-4 h-4" /> Operating airlines
              </h3>
              <div className="flex flex-wrap gap-2">
                {operatingAirlines.map((al) => <AirlinePill key={al.airline_id} airline={al} />)}
              </div>
            </section>
          )}

          {relatedAirports.length > 0 && (
            <section className="rounded-xl border border-slate-200 bg-white p-5">
              <h3 className="text-sm font-bold uppercase tracking-widest text-slate-500 mb-3 inline-flex items-center gap-2">
                <Globe2 className="w-4 h-4" /> Connected airports
              </h3>
              <ul className="space-y-2">
                {relatedAirports.slice(0, 12).map((ra) => (
                  <li key={ra.airport_id}>
                    <Link
                      to={`/aviation/airport/${ra.airport_id}`}
                      className="flex items-center gap-2 text-sm text-slate-700 hover:text-blue-700"
                      data-testid={`airport-related-${ra.airport_id}`}
                    >
                      <span className="inline-flex rounded-md bg-sky-50 border border-sky-100 text-sky-700 text-[10px] font-bold px-1.5 py-0.5">
                        {ra.iata || "—"}
                      </span>
                      <span className="truncate">{ra.canonical_name}</span>
                    </Link>
                  </li>
                ))}
              </ul>
            </section>
          )}
        </aside>
      </div>
    </div>
  );
}
